import './filter-dropdown.scss';
import AjaxLoad from '../../lib/ajax-load';
import { getOffset, smoothScrollTo } from '../../js/helper';

class FilterDropdown {
    constructor (root) {
        this.$container = root;
        this.$trigger = root.querySelector('[data-filter-trigger]');
        this.$menu = root.querySelector('[data-filter-menu]');
        this.$searchField = root.querySelector('[data-filter-search]');
        this.$badge = root.querySelector('[data-filter-badge]');
        this.$$listItems = root.querySelectorAll('[data-filter-item]');
        this.$$inputs = root.querySelectorAll('input[type="checkbox"]');
        this.$confirmButton = root.querySelector('[data-filter-confirm]');
        this.$resetButton = document.querySelector('[data-filter-reset]');

        this.$overview = document.querySelector('[data-overview="root"]');
        this.$target = document.querySelector('[data-teaser-grid="root"]');

        this.customEvent = new CustomEvent('ajaxload');
    }

    initialize () {
        if (!this.$container || !this.$trigger || !this.$menu || this.isInitialized) return this;

        this.isInitialized = true;

        // URL-Parameter beim Initialisieren prüfen
        this.checkInitialFilters();

        // Event-Listener binden
        this.$trigger.addEventListener('click', () => this.toggle());

        if (this.$searchField) {
            this.$searchField.addEventListener('input', (e) => this.filterList(e.target.value));
        }

        if (this.$confirmButton) {
            this.$confirmButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.updateBadge();
                this.handleFilterChange();
                this.toggle(false);
            });
        }

        if (this.$resetButton) {
            this.$resetButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.resetFilters();
            });
        }

        // Schließen bei Klick außerhalb oder ESC
        document.addEventListener('click', (e) => this.handleOutsideClick(e));
        this.$container.addEventListener('keydown', (e) => this.handleKeyboard(e));

        this.updateBadge();
        this.updateTags();

        return this;
    }

    checkInitialFilters () {
        const params = new URLSearchParams(window.location.search);
        this.$$inputs.forEach(input => {
            if (params.has(input.name)) {
                const values = params.getAll(input.name);
                if (values.includes(input.value)) {
                    input.checked = true;
                }
            }
        });
    }

    updateBadge () {
        if (!this.$badge) return;

        const count = Array.from(this.$$inputs).filter(input => input.checked).length;
        this.$badge.textContent = count;
        this.$badge.hidden = count === 0;
    }

    updateTags () {
        const $tagList = document.querySelector('.form-filters__tags-list');
        if (!$tagList) return;

        this.$$inputs.forEach(input => {
            const tagId = `tag-${input.name}-${input.value}`;
            const $existingTag = $tagList.querySelector(`[data-tag-id="${tagId}"]`);

            if (input.checked) {
                if (!$existingTag) {
                    const labelText = input.closest('label').querySelector('.filter-dropdown__checkbox-label').textContent.trim();
                    const $tag = document.createElement('div');
                    $tag.className = 'form-filters__tags-item';
                    $tag.setAttribute('data-tag-id', tagId);
                    $tag.innerHTML = `
                        <span>${labelText}</span>
                        <button type="button" data-filter-tag-remove><span class="screenreader-only">Filter entfernen</span></button>
                    `;

                    $tag.querySelector('[data-filter-tag-remove]').addEventListener('click', () => {
                        input.checked = false;
                        FilterDropdown.updateAll();
                        this.handleFilterChange();
                    });

                    $tagList.appendChild($tag);
                }
            } else {
                if ($existingTag) {
                    $existingTag.remove();
                }
            }
        });

        // Toggle reset button visibility
        if (this.$resetButton) {
            // Check overall if any checkbox in the form is checked (not just in this instance)
            const allChecked = document.querySelectorAll('.form-filters__form input[type="checkbox"]:checked').length > 0;
            this.$resetButton.hidden = !allChecked;
        }
    }

    resetFilters () {
        const $form = this.$container.closest('form');
        if ($form) {
            const $allInputs = $form.querySelectorAll('input[type="checkbox"]');
            $allInputs.forEach(input => {
                input.checked = false;
            });
        }

        FilterDropdown.updateAll();
        this.handleFilterChange();
    }

    handleFilterChange () {
        const form = this.$container.closest('form');
        let url = window.location.href.split('?')[0];

        if (form) {
            const formData = new FormData(form);
            const params = new URLSearchParams();

            formData.forEach((value, key) => {
                if (value) {
                    params.append(key, value);
                }
            });

            const queryString = params.toString();
            if (queryString) {
                url += '?' + queryString;
            }
        }

        FilterDropdown.updateAll();
        this.loadContent(url, 'GET');
    }

    static updateAll () {
        document.querySelectorAll('[data-filter-widget]').forEach($el => {
            if ($el.API) {
                $el.API.updateBadge();
                $el.API.updateTags();
            }
        });
    }

    loadContent (url, method) {
        if (!this.$overview || !this.$target) {
            // Fallback: If overview or target not found, just reload page
            window.location.href = url;
            return;
        }

        this.$overview.classList.add('is--loading');
        smoothScrollTo(0, getOffset(this.$overview).top - 150, 500);

        const ajaxLoad = new AjaxLoad(url, {
            requestMethod: method,
            onSuccess: (resultText) => {
                this.setContent(this.createElementFromHTML(resultText), method, () => {
                    history.pushState({ type: 'ajax' }, null, window.location.href);
                    history.replaceState({ type: 'ajax' }, null, url);
                });
            }
        });

        ajaxLoad.initialize();
    }

    createElementFromHTML (htmlString) {
        const div = document.createElement('div');
        div.innerHTML = htmlString.trim();

        if (div.children.length > 1) {
            const itemsArray = [];
            Array.prototype.push.apply(itemsArray, div.children);
            return itemsArray;
        } else {
            return div.firstChild;
        }
    }

    setContent (resultText, method, callback) {
        this.$target.innerHTML = resultText.innerHTML;

        setTimeout(() => {
            this.$overview.classList.remove('is--loading');
            window.dispatchEvent(this.customEvent);

            if (typeof callback === 'function') {
                callback();
            }

            const contentUpdateEvent = new CustomEvent('content.loaded', {
                view: window,
                bubbles: true,
                cancelable: false,
                detail: {
                    $context: this.$target
                }
            });

            window.dispatchEvent(contentUpdateEvent);
            // window.initContent is often globally defined in these types of projects
            if (typeof window.initContent === 'function') {
                window.initContent(this.$target);
            }
        }, 1100);
    }

    toggle (forceState) {
        const isCurrentlyExpanded = this.$trigger.getAttribute('aria-expanded') === 'true';
        const newState = forceState !== undefined ? forceState : !isCurrentlyExpanded;

        if (isCurrentlyExpanded === newState) return;

        this.$trigger.setAttribute('aria-expanded', newState);
        this.$menu.hidden = !newState;

        if (newState) {
            // Fokus-Management beim Öffnen
            if (this.$searchField) {
                this.$searchField.focus();
            } else {
                const firstInput = this.$menu.querySelector('input, button');
                if (firstInput) firstInput.focus();
            }
        } else {
            // Beim Schließen Fokus zurück auf den Trigger, wenn der Fokus innerhalb des Menüs war
            if (this.$container.contains(document.activeElement)) {
                this.$trigger.focus();
            }
        }
    }

    filterList (query) {
        const filter = query.toLowerCase();
        this.$$listItems.forEach(item => {
            const text = item.textContent.toLowerCase();
            item.style.display = text.includes(filter) ? '' : 'none';
        });
    }

    handleOutsideClick (e) {
        if (!this.$container.contains(e.target)) {
            this.toggle(false);
        }
    }

    handleKeyboard (e) {
        if (e.key === 'Escape') {
            this.toggle(false);
            this.$trigger.focus();
        }
    }
}

window.addEventListener('content.loaded', (e) => {
    const { $context } = e.detail;
    if (!$context) return;

    $context.querySelectorAll('[data-filter-widget]').forEach(($el) => {
        $el.API = ($el.API ?? new FilterDropdown($el)).initialize();
    });
});
